import { Checkbox, cn } from "ui";
import { useField } from "../hooks/useField";
import { useFormFieldContext } from "./FormField";

type CheckboxFormFieldProps = {
  customLabel?: React.ReactNode;
  description?: React.ReactNode;
  variant?: React.ComponentProps<typeof Checkbox>["variant"];
  className?: string;
} & (
  | {
      id: string;
      value: string;
      multiple: true;
    }
  | {
      id?: never;
      value?: never;
      multiple?: never;
    }
);

export function CheckboxFormField({
  id,
  value: checkboxValue,
  multiple,
  customLabel,
  variant,
  description,
  className,
}: CheckboxFormFieldProps) {
  const {
    name,
    disabled,
    readOnly,
    required,
    defaultValue = multiple ? [] : false,
    validation,
    customValidation,
    descriptionId,
    errorMessageId,
    shouldUnregister,
    label: contextLabel,
  } = useFormFieldContext();

  const { ref, value, onChange, onBlur, error } = useField({
    name,
    defaultValue,
    validation,
    customValidation,
    shouldUnregister,
  });

  return (
    <Checkbox
      ref={ref}
      id={id || name}
      label={customLabel || contextLabel}
      description={description}
      name={name}
      checked={multiple ? value.includes(checkboxValue) : value}
      onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
        if (multiple) {
          const newValue = e.target.checked
            ? [...value, checkboxValue]
            : value.filter((v: string) => v !== checkboxValue);
          onChange(newValue);
        } else {
          onChange(e.target.checked);
        }
      }}
      onBlur={onBlur}
      className={cn(
        error &&
          "border-danger-bold focus:border-danger-bold focus:ring-danger-bold"
      )}
      containerClassName={className}
      disabled={disabled}
      readOnly={readOnly}
      required={required}
      aria-describedby={
        !error ? `${descriptionId}` : `${descriptionId} ${errorMessageId}`
      }
      aria-invalid={!!error}
      variant={variant}
    />
  );
}
